import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import Icon from './Icon.jsx';
import styles from './Sheet.module.scss';

const CLOSE_DISTANCE = 110;
const CLOSE_VELOCITY = 0.6;
const stack = [];

/** Bottom sheet on phones (drag the top down to dismiss), centered dialog on tablet and up. */
export default function Sheet({ open, onClose, title, subtitle, children, footer, wide = false, dismissible = true }) {
  const panelRef = useRef(null);
  const drag = useRef(null);
  const endDrag = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.activeElement;
    const token = {};
    stack.push(token);
    const onKey = (e) => {
      if (dismissible && e.key === 'Escape' && stack[stack.length - 1] === token) {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => {
      const panel = panelRef.current;
      if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
    }, 30);
    document.documentElement.classList.add('sheet-open');
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      stack.splice(stack.indexOf(token), 1);
      if (!stack.length) document.documentElement.classList.remove('sheet-open');
      endDrag.current?.();
      prev?.focus?.({ preventScroll: true });
    };
  }, [open, onClose, dismissible]);

  if (!open) return null;

  const onPointerDown = (e) => {
    if (!dismissible || e.pointerType === 'mouse' || e.target.closest('button')) return;
    endDrag.current?.();
    const pointerId = e.pointerId;
    const originY = e.clientY;
    const started = performance.now();
    const move = (ev) => {
      if (ev.pointerId !== pointerId || !panelRef.current) return;
      const dy = Math.max(0, ev.clientY - originY);
      drag.current = { dy, t: started };
      panelRef.current.style.transition = 'none';
      panelRef.current.style.transform = `translateY(${dy}px)`;
    };
    const up = (ev) => {
      if (ev.pointerId !== pointerId) return;
      stop();
      const d = drag.current;
      drag.current = null;
      const panel = panelRef.current;
      if (!d || !panel) return;
      const v = d.dy / Math.max(1, performance.now() - d.t);
      panel.style.transition = '';
      if (d.dy > CLOSE_DISTANCE || (d.dy > 30 && v > CLOSE_VELOCITY)) {
        panel.style.transform = 'translateY(100%)';
        setTimeout(() => onClose?.(), 160);
      } else {
        panel.style.transform = '';
      }
    };
    const stop = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      window.removeEventListener('pointercancel', up, true);
      if (endDrag.current === stop) endDrag.current = null;
    };
    endDrag.current = stop;
    drag.current = { dy: 0, t: started };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', up, true);
  };

  return createPortal(
    <div className={styles.backdrop} onMouseDown={(e) => dismissible && e.target === e.currentTarget && onClose?.()}>
      <div
        ref={panelRef}
        className={clsx(styles.panel, wide && styles.wide)}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className={styles.handleArea} onPointerDown={onPointerDown}>
          <div className={styles.grip} aria-hidden="true" />
          <header className={styles.header}>
            <div className={styles.titles}>
              <h2>{title}</h2>
              {subtitle ? <p>{subtitle}</p> : null}
            </div>
            {dismissible ? (
              <button type="button" className={styles.close} onClick={onClose} data-close>
                <Icon name="close" size={20} stroke={2.5} />
                <span className="visually-hidden">Close</span>
              </button>
            ) : null}
          </header>
        </div>
        <div className={styles.body}>{children}</div>
        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
